import {use, useEffect, useLayoutEffect, useRef, useState} from 'react'

import {ExperimentScopeContext, ExperimentStoreContext} from './experiment-context'
import {type ExperimentScope, type ExperimentStore, unavailableExperimentScope} from './internal'

// Browser layout effects keep navigation commits in the same phase as decision pinning.
const useCommitEffect = typeof document === 'undefined' ? useEffect : useLayoutEffect

const resolveScope = (
  isNewDestination: boolean,
  documentScope: ExperimentScope,
  resolveCandidate?: () => ExperimentScope | undefined,
) => {
  if (!isNewDestination) return documentScope
  return resolveCandidate?.() ?? unavailableExperimentScope()
}

const resolveAcceptedScope = ({
  acceptedDestinationKey,
  committedDestinationKey,
  stagedNavigationId,
  documentScope,
  store,
  resolveCandidate,
}: {
  acceptedDestinationKey: string
  committedDestinationKey: string
  stagedNavigationId?: string
  documentScope: ExperimentScope
  store: ExperimentStore
  resolveCandidate?: () => ExperimentScope | undefined
}) => {
  if (acceptedDestinationKey === committedDestinationKey) return documentScope
  if (stagedNavigationId === acceptedDestinationKey) {
    return store.getNavigationCandidate(acceptedDestinationKey)
  }
  return resolveCandidate?.() ?? unavailableExperimentScope()
}

type NavigationScopeTransactionOptions = {
  /** Identifies the destination currently requested by the router. */
  destinationKey: string
  /**
   * Identifies the destination whose route tree is currently rendered. It differs from
   * destinationKey while a Data Router navigation is pending.
   *
   * Defaults to destinationKey for routers such as Navigator that publish only accepted destinations.
   */
  acceptedDestinationKey?: string
  /** Returns the requested destination's assignment scope, or undefined when none was provided. */
  resolveCandidate?: () => ExperimentScope | undefined
}

type NavigationScopeTransaction = {
  /** The assignment scope that descendants should use for the current render. */
  scope: ExperimentScope
}

export function useNavigationScopeTransaction({
  destinationKey,
  acceptedDestinationKey = destinationKey,
  resolveCandidate,
}: NavigationScopeTransactionOptions): NavigationScopeTransaction {
  const store = use(ExperimentStoreContext)
  const documentScope = use(ExperimentScopeContext)
  if (!store || !documentScope) {
    throw new Error('useNavigationScopeTransaction must be used within ExperimentDocumentProvider')
  }
  const [committedDestinationKey, setCommittedDestinationKey] = useState(acceptedDestinationKey)
  const [stagedNavigationId, setStagedNavigationId] = useState<string>()
  const stagedNavigationIdRef = useRef<string | undefined>(undefined)
  const pendingNavigationId = destinationKey !== acceptedDestinationKey ? destinationKey : undefined
  const isNewAcceptedDestination = acceptedDestinationKey !== committedDestinationKey
  const scope = resolveAcceptedScope({
    acceptedDestinationKey,
    committedDestinationKey,
    stagedNavigationId,
    documentScope,
    store,
    resolveCandidate,
  })
  const pendingScope = resolveScope(pendingNavigationId !== undefined, documentScope, resolveCandidate)

  useCommitEffect(() => {
    let nextStagedNavigationId = stagedNavigationId
    if (isNewAcceptedDestination) {
      if (nextStagedNavigationId !== acceptedDestinationKey) {
        if (nextStagedNavigationId) {
          store.discardNavigation(nextStagedNavigationId)
        }
        store.startNavigation(acceptedDestinationKey)
        store.captureNavigationCandidate(acceptedDestinationKey, scope)
      }
      store.commitNavigation(acceptedDestinationKey)
      setCommittedDestinationKey(acceptedDestinationKey)
      nextStagedNavigationId = undefined
    }

    if (!pendingNavigationId) {
      // The requested destination was cancelled or the accepted destination has caught up.
      if (nextStagedNavigationId) {
        store.discardNavigation(nextStagedNavigationId)
      }
      stagedNavigationIdRef.current = undefined
      setStagedNavigationId(undefined)
      return
    }

    if (nextStagedNavigationId !== pendingNavigationId) {
      // A newer request supersedes any candidate that was not accepted.
      if (nextStagedNavigationId) {
        store.discardNavigation(nextStagedNavigationId)
      }
      store.startNavigation(pendingNavigationId)
    }
    store.captureNavigationCandidate(pendingNavigationId, pendingScope)
    stagedNavigationIdRef.current = pendingNavigationId
    setStagedNavigationId(pendingNavigationId)
  }, [
    acceptedDestinationKey,
    isNewAcceptedDestination,
    pendingNavigationId,
    pendingScope,
    scope,
    stagedNavigationId,
    store,
  ])

  useCommitEffect(
    () => () => {
      const navigationIdAtUnmount = stagedNavigationIdRef.current
      if (navigationIdAtUnmount) {
        store.discardNavigation(navigationIdAtUnmount)
      }
      stagedNavigationIdRef.current = undefined
    },
    [store],
  )

  return {scope}
}
